import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import type { GameMetadata, ItemDetailDto, ItemDraft, ItemStatus, ItemType } from '../api/types';
import { ConfirmDialog } from '../components/Modal';
import { Cover } from '../components/cards';
import { useToast } from '../components/Toast';
import { EmptyState, ErrorState, Field, Loading, Select, TextArea, TextInput, TypeBadge, VisibilityBadge } from '../components/ui';
import { IconExternal, IconLink, IconPencil, IconTrash } from '../components/icons';
import { useAsync } from '../hooks/useAsync';
import { t } from '../i18n';
import { formatDate, hostOf, messageOf, statusLabel, statusOptions, typeLabel, typeOptions } from '../lib/format';

const PORTRAIT_TYPES = ['book', 'movie', 'series', 'game'];

/** Where the back link leads: the list this card was opened from, its filter included. */
function backPath(state: unknown): string {
  const from = (state as { from?: unknown } | null)?.from;
  return typeof from === 'string' && from.startsWith('/') && !from.startsWith('//') ? from : '/app/library';
}

function gameRows(item: ItemDetailDto): [string, string][] {
  const game = (item.metadata as { game?: GameMetadata } | null)?.game;
  if (!game) return [];
  return (
    [
      [t('item.developer'), game.developers.join(', ')],
      [t('item.publisher'), game.publishers.join(', ')],
      [t('item.genre'), game.genres.join(', ')],
      [t('item.platforms'), game.platforms.join(', ')],
      [t('item.year'), game.releaseYear ?? ''],
    ] as [string, string][]
  ).filter(([, value]) => value);
}

function toDraft(item: ItemDetailDto): ItemDraft {
  return {
    url: item.url,
    title: item.title,
    description: item.description ?? '',
    imageUrl: item.imageUrl ?? '',
    type: item.type,
    status: item.status ?? '',
    author: item.author ?? '',
    personalNote: item.personalNote ?? '',
  };
}

export function ItemPage() {
  const { id = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const state = useAsync(() => api.items.get(id), [id]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ItemDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showMeta, setShowMeta] = useState(false);

  const item = state.data;

  if (state.error) {
    return (
      <div className="page">
        <ErrorState error={state.error} onRetry={state.reload} />
      </div>
    );
  }
  if (state.loading || !item) {
    return (
      <div className="page">
        <Loading />
      </div>
    );
  }

  const form = draft ?? toDraft(item);

  async function save() {
    setBusy(true);
    try {
      await api.items.update(item!.id, form);
      toast.success(t('item.updated'));
      setEditing(false);
      state.reload();
    } catch (error) {
      toast.error(messageOf(error));
    } finally {
      setBusy(false);
    }
  }

  async function reparse() {
    setBusy(true);
    try {
      const fresh = await api.items.parse(item!.url);
      // The type is a deliberate choice once set — a re-parse may suggest, not overwrite.
      const keepType = item!.type !== 'other' && fresh.type !== item!.type;
      setDraft({
        ...toDraft(item!),
        title: fresh.title ?? item!.title,
        description: fresh.description ?? item!.description ?? '',
        imageUrl: fresh.imageUrl ?? item!.imageUrl ?? '',
        type: keepType ? item!.type : (fresh.type ?? item!.type),
        author: fresh.author ?? item!.author ?? '',
        game: fresh.game,
      });
      setEditing(true);
      if (!fresh.warning && keepType && fresh.type) {
        toast.push(t('item.similarType', { found: typeLabel(fresh.type), kept: typeLabel(item!.type) }));
      } else {
        toast.push(fresh.warning ?? t('item.reparsed'));
      }
    } catch (error) {
      toast.error(messageOf(error));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await api.items.remove(item!.id);
      toast.success(t('item.deleted'));
      navigate('/app/library', { replace: true });
    } catch (error) {
      toast.error(messageOf(error));
      setBusy(false);
      setConfirmDelete(false);
    }
  }

  return (
    <div className="page">
      <p className="tiny" style={{ marginBottom: 'var(--sp-4)' }}>
        <Link to={backPath(location.state)}>{t('item.back')}</Link>
      </p>

      <div className="item-detail">
        <div className="stack">
          <Cover
            src={item.imageUrl}
            title={item.title}
            className={`item-detail__cover${PORTRAIT_TYPES.includes(item.type) ? ' cover--portrait' : ''}`}
            eager
          />
          <div className="row row--tight">
            <TypeBadge type={item.type} />
            {item.sourceName ? (
              <span className="badge">
                <span className="dot" aria-hidden="true" /> {item.sourceName}
              </span>
            ) : null}
          </div>
          <div className="kv">
            <div className="kv__row">
              <span className="kv__key">{t('item.savedAt')}</span>
              <span className="kv__val">{formatDate(item.createdAt)}</span>
            </div>
            {item.status ? (
              <div className="kv__row">
                <span className="kv__key">{t('item.status')}</span>
                <span className="kv__val">{statusLabel(item.type, item.status)}</span>
              </div>
            ) : null}
            {item.author ? (
              <div className="kv__row">
                <span className="kv__key">{t('item.author')}</span>
                <span className="kv__val">{item.author}</span>
              </div>
            ) : null}
            {gameRows(item).map(([key, value]) => (
              <div className="kv__row" key={key}>
                <span className="kv__key">{key}</span>
                <span className="kv__val">{value}</span>
              </div>
            ))}
            <div className="kv__row">
              <span className="kv__key">{t('item.domain')}</span>
              <span className="kv__val">{hostOf(item.url)}</span>
            </div>
          </div>
          <a className="btn btn--outline btn--block" href={item.url} target="_blank" rel="noreferrer noopener">
            <IconExternal size={17} /> {t('item.openSource')}
          </a>
          <button type="button" className="btn btn--ghost btn--block" onClick={reparse} disabled={busy}>
            <IconLink size={17} /> {t('item.reparse')}
          </button>
        </div>

        <div className="stack">
          {editing ? (
            <div className="panel stack">
              <Field label={t('playlists.fieldTitle')} htmlFor="item-title">
                <TextInput
                  id="item-title"
                  value={form.title}
                  onChange={(event) => setDraft({ ...form, title: event.target.value })}
                />
              </Field>
              <Field label={t('item.type')} htmlFor="item-type">
                <Select
                  id="item-type"
                  value={form.type}
                  onChange={(event) => setDraft({ ...form, type: event.target.value as ItemType })}
                >
                  {typeOptions().map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('item.status')} htmlFor="item-status" hint={t('item.statusHint')}>
                <Select
                  id="item-status"
                  value={form.status}
                  onChange={(event) => setDraft({ ...form, status: event.target.value as ItemStatus | '' })}
                >
                  <option value="">{t('item.statusNone')}</option>
                  {statusOptions(form.type).map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('item.url')} htmlFor="item-url">
                <TextInput
                  id="item-url"
                  type="url"
                  value={form.url}
                  onChange={(event) => setDraft({ ...form, url: event.target.value })}
                />
              </Field>
              <Field label={t('playlists.fieldDescription')} htmlFor="item-desc">
                <TextArea
                  id="item-desc"
                  rows={4}
                  value={form.description}
                  onChange={(event) => setDraft({ ...form, description: event.target.value })}
                />
              </Field>
              <Field label={t('item.coverUrl')} htmlFor="item-image">
                <TextInput
                  id="item-image"
                  type="url"
                  value={form.imageUrl}
                  onChange={(event) => setDraft({ ...form, imageUrl: event.target.value })}
                />
              </Field>
              <Field label={t('item.author')} htmlFor="item-author">
                <TextInput
                  id="item-author"
                  value={form.author}
                  onChange={(event) => setDraft({ ...form, author: event.target.value })}
                />
              </Field>
              <Field label={t('item.note')} htmlFor="item-note" hint={t('item.noteHint')}>
                <TextArea
                  id="item-note"
                  rows={3}
                  value={form.personalNote}
                  onChange={(event) => setDraft({ ...form, personalNote: event.target.value })}
                />
              </Field>
              <div className="row">
                <button type="button" className="btn btn--primary" onClick={save} disabled={busy}>
                  {busy ? t('item.saving') : t('item.save')}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => {
                    setEditing(false);
                    setDraft(null);
                  }}
                >
                  {t('ui.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <div className="stack">
              <div className="row row--between">
                <h1 style={{ fontSize: 'var(--fs-24)' }}>{item.title}</h1>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => {
                    setDraft(toDraft(item));
                    setEditing(true);
                  }}
                >
                  <IconPencil size={16} /> {t('item.edit')}
                </button>
              </div>
              {item.description ? <p className="dim">{item.description}</p> : null}
              {item.personalNote ? (
                <div className="notice">
                  <span>
                    <span className="caps">{t('item.yourNote')}</span>
                    <p style={{ marginTop: 'var(--sp-1)' }}>{item.personalNote}</p>
                  </span>
                </div>
              ) : null}
            </div>
          )}

          <section>
            <div className="section__head">
              <h2 style={{ fontSize: 'var(--fs-18)' }}>{t('item.inPlaylists')}</h2>
            </div>
            {item.appearsIn.length === 0 ? (
              <p className="muted small">
                {t('item.nowhere')}{' '}
                <Link to="/app/playlists/new">{t('item.buildOne')}</Link> {t('item.aroundLink')}
              </p>
            ) : (
              <ul className="stack-sm">
                {item.appearsIn.map((playlist) => (
                  <li key={playlist.id}>
                    <Link className="list-row" to={`/p/${playlist.id}`}>
                      <span className="strong">{playlist.title}</span>
                      <VisibilityBadge visibility={playlist.visibility} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setShowMeta((value) => !value)}>
              {showMeta ? t('item.hideParser') : t('item.showParser')}
            </button>
            {showMeta ? (
              <pre className="meta-json">{JSON.stringify(item.metadata, null, 2).slice(0, 4000)}</pre>
            ) : null}
          </section>

          <hr className="divider" />

          <div className="row row--between">
            <span className="tiny muted">{t('item.deleteWarn')}</span>
            <button type="button" className="btn btn--danger btn--sm" onClick={() => setConfirmDelete(true)}>
              <IconTrash size={16} /> {t('ui.delete')}
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title={t('item.deleteTitle')}
        description={t('item.deleteBody', { title: item.title })}
        busy={busy}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
      />
    </div>
  );
}

export function ItemNotFound() {
  return (
    <div className="page">
      <EmptyState title={t('item.notFound')} body={t('item.notFoundBody')} />
    </div>
  );
}
